import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, EyeOff, FileText, Image as ImmagineIcona, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useConferma } from "@/hooks/use-conferma";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { eur } from "@/lib/formato";
import { erroreDi, giorno } from "@/lib/crm";
import { FIRMATI, statoContratto } from "@/lib/soldi";
import { apriVersione, eImmagine, erroreDocumenti, fraseEvento } from "@/lib/documenti";
import { cn } from "@/lib/utils";
import CaricaDocumento from "./CaricaDocumento";
import DettaglioDocumento from "./DettaglioDocumento";

function riduciMovimento() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function RigaDocumento({ doc, onApri }) {
  const a = doc.attuale;
  const Icona = eImmagine(a.mime) ? ImmagineIcona : FileText;
  const dettaglio = [
    doc.tipo !== doc.categoria ? doc.tipo_etichetta : null,
    a.versione > 1 ? `versione ${a.versione}` : null,
    a.caricato_da_nome,
    giorno(a.created_at),
  ].filter(Boolean);
  return (
    <li>
      <button
        type="button"
        onClick={() => onApri(doc.id)}
        data-testid={`documento-${doc.id}`}
        className="flex min-h-11 w-full items-center gap-group rounded-md px-inside py-inside text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icona className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{doc.nome}</p>
          <p className="truncate text-xs text-muted-foreground">{dettaglio.join(" · ")}</p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
    </li>
  );
}

/* Una riga dello storico. `apri` è { to } (un indirizzo), { onClick } (un file) o niente; `togli` è la
   funzione del «Togli», solo per le righe fatte fuori e per chi può. */
function RigaPiattaforma({ titolo, sotto, importo, segno, apri, togli, testId }) {
  const dentro = (
    <>
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-inside text-sm font-medium text-foreground">
          <span className="min-w-0 break-words">{titolo}</span>
          {segno ? <StatusBadge>{segno}</StatusBadge> : null}
        </p>
        <p className="text-xs text-muted-foreground">{sotto}</p>
      </div>
      <span className="text-sm tabular-nums text-foreground">{importo}</span>
    </>
  );
  const classi = "flex min-h-11 min-w-0 flex-1 items-center gap-group rounded-md px-inside py-inside text-left transition-colors";
  const attivo = cn(classi, "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring");
  return (
    <li className="flex items-center gap-inside" data-testid={testId}>
      {apri?.to ? (
        <Link to={apri.to} className={attivo}>
          {dentro}
        </Link>
      ) : apri?.onClick ? (
        <button type="button" onClick={apri.onClick} className={attivo}>
          {dentro}
        </button>
      ) : (
        <div className={classi}>{dentro}</div>
      )}
      {togli ? (
        <Button type="button" variant="ghost" size="sm" onClick={togli} className="shrink-0">
          Togli
        </Button>
      ) : null}
    </li>
  );
}

const Affordance = ({ children }) => <span className="underline underline-offset-2">{children}</span>;

/* Preventivi, contratti e fatture: li fa la piattaforma, non si caricano. Stavano
   già nella sezione Documenti della scheda e restano qui, sotto l'archivio. I preventivi e i contratti
   «fatti fuori» (c36) hanno il segno e il loro file: si aprono da qui, e chi li ha registrati può toglierli. */
function DallaPiattaforma({ generati, onCambiato, chi }) {
  const { preventivi = [], contratti = [], fatture = [], allegati = [] } = generati || {};
  const { chiedi, dialogoConferma } = useConferma();
  if (!preventivi.length && !contratti.length && !fatture.length) return null;
  const titolare = chi?.ruolo === "titolare";
  const fileDi = (campo, id) => allegati.find((a) => a[campo] === id && !a.tolto_at);
  const apriFile = (a) => () => apriVersione(a.id).catch(() => toast.error("Il file non si è aperto: riprova"));
  const togli = (cosa, id, domanda) => async () => {
    if (!(await chiedi({ ...domanda, verbo: "Sì, toglilo" }))) return;
    try {
      await api.delete(`/crm/fatto-fuori/${cosa}/${id}`);
      toast.success(cosa === "preventivo" ? "Preventivo tolto" : "Contratto tolto");
      onCambiato?.();
    } catch (e) {
      toast.error(erroreDi(e));
    }
  };
  return (
    <section className="grid grid-cols-1 gap-micro" aria-labelledby="documenti-piattaforma-titolo">
      <h3 id="documenti-piattaforma-titolo" className="text-xs font-medium text-muted-foreground">
        Dalla piattaforma
      </h3>
      <ul className="-mx-inside grid grid-cols-1">
        {preventivi.map((p) => {
          const file = p.esterno ? fileDi("preventivo_id", p.id) : null;
          const inviato = p.inviato_at ? `inviato ${giorno(p.inviato_at)}` : p.stato;
          return (
            <RigaPiattaforma
              key={p.id}
              testId={`storico-preventivo-${p.id}`}
              titolo={p.titolo || p.numero || "Preventivo"}
              segno={p.esterno ? "Fatto fuori" : null}
              sotto={
                file ? (
                  <>
                    Preventivo · {inviato} · <Affordance>apri il file</Affordance>
                  </>
                ) : (
                  `Preventivo · ${inviato}`
                )
              }
              importo={eur(p.totale, { decimali: 0, vuoto: "" })}
              apri={p.esterno ? (file ? { onClick: apriFile(file) } : null) : { to: `/preventivi/preventivatore/${p.id}` }}
              togli={
                p.esterno && !p.chiuso_da_contratto_id && (titolare || (chi?.id && p.inviato_da === chi.id))
                  ? togli("preventivo", p.id, {
                      titolo: "Togliere il preventivo?",
                      descrizione: "Il preventivo fatto fuori esce dal cliente e il suo file va fra i documenti tolti. Il cliente torna dove dicono i fatti rimasti.",
                    })
                  : null
              }
            />
          );
        })}
        {contratti.map((k) => {
          const file = k.esterno ? fileDi("contratto_id", k.id) : null;
          const stato = FIRMATI.has(k.stato) && k.data_firma ? `firmato ${giorno(k.data_firma)}` : statoContratto(k.stato).label.toLowerCase();
          return (
            <RigaPiattaforma
              key={k.id}
              testId={`storico-contratto-${k.id}`}
              titolo={`Contratto ${k.numero || ""}`}
              segno={k.esterno ? "Fatto fuori" : null}
              sotto={
                file ? (
                  <>
                    {stato} · <Affordance>Apri il firmato</Affordance>
                  </>
                ) : (
                  stato
                )
              }
              importo={eur(k.totale, { decimali: 0, vuoto: "" })}
              apri={k.esterno ? (file ? { onClick: apriFile(file) } : null) : chi?.contratti ? { to: `/contratti?c=${k.id}` } : null}
              togli={
                k.esterno && titolare && k.stato !== "annullato" // già tolto: resta nello storico come annullato
                  ? togli("contratto", k.id, {
                      titolo: "Togliere il contratto?",
                      descrizione:
                        "Il contratto fatto fuori esce, con le sue rate, e il suo file va fra i documenti tolti. I preventivi che chiudeva e la vendita di prima tornano liberi. Si può solo se non ci sono incassi, fatture o ordini.",
                    })
                  : null
              }
            />
          );
        })}
        {fatture.map((f) => (
          <RigaPiattaforma
            key={f.id}
            titolo={`Fattura ${f.numero || ""}`}
            sotto={`${f.tipo}${f.data_emissione ? ` · ${giorno(f.data_emissione)}` : ""}`}
            importo={eur(f.totale, { decimali: 0, vuoto: "" })}
          />
        ))}
      </ul>
      {dialogoConferma}
    </section>
  );
}

function TogliDialogo({ documento, onChiudi, onTolto }) {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (documento) setMotivo("");
  }, [documento]);
  const togli = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/documenti/${documento.id}/togli`, { motivo: motivo.trim() || null });
      onTolto();
    } catch (err) {
      toast.error(erroreDocumenti(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={documento != null} onOpenChange={(v) => !v && !busy && onChiudi()}>
      <DialogContent className="sm:max-w-md" data-testid="documenti-togli-dialogo">
        <form onSubmit={togli} className="grid gap-card">
          <DialogHeader className="pr-card">
            <DialogTitle>Togliere il documento?</DialogTitle>
            <DialogDescription>
              «{documento?.nome}» esce dall'elenco ma non si cancella: resta fra i tolti con il tuo nome e l'ora, e il titolare
              può rimetterlo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-inside">
            <Label htmlFor="documenti-motivo">Perché (facoltativo)</Label>
            <Textarea
              id="documenti-motivo"
              rows={2}
              maxLength={500}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Es. caricato sul cliente sbagliato, è un doppione"
              data-testid="documenti-motivo"
            />
          </div>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" className="h-11 sm:h-9" disabled={busy} onClick={onChiudi}>
              Annulla
            </Button>
            <Button type="submit" variant="destructive" className="h-11 sm:h-9" disabled={busy} data-testid="documenti-togli-conferma">
              {busy ? "Tolgo…" : "Togli dall'archivio"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * L'archivio documenti del cliente, nella sua scheda.
 *
 * `/clienti/<id>?documenti=<categoria>` apre il caricamento già su quella
 * categoria: è il collegamento «Carica il firmato» del contratto.
 *
 * `chi` = { id, ruolo, contratti }: chi guarda, e se la sua sezione Contratti è accesa. Serve allo storico
 * «Dalla piattaforma»: il link al contratto e il «Togli» dei fatti fuori dipendono da lì. `onCambiato` si
 * chiama dopo un «Togli», perché la scheda si ricarichi.
 */
export default function ArchivioDocumenti({ clienteId, generati, onCambiato, chi }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [carica, setCarica] = useState(null);
  const [apertoId, setApertoId] = useState(null);
  const [daTogliere, setDaTogliere] = useState(null);
  const [mostraTolti, setMostraTolti] = useState(false);
  const [params, setParams] = useSearchParams();
  const card = useRef(null);

  const leggi = useCallback(
    () =>
      api
        .get(`/documenti/clienti/${clienteId}`)
        .then((r) => {
          setDati(r.data);
          setErrore(null);
        })
        .catch((e) => setErrore(erroreDocumenti(e))),
    [clienteId],
  );
  // `generati` cambia quando la scheda si ricarica (un «Fatto fuori», un «Togli»): anche i file si rileggono.
  useEffect(() => {
    leggi();
  }, [leggi, generati]);

  const richiesta = params.get("documenti");
  useEffect(() => {
    if (!richiesta || !dati) return;
    card.current?.scrollIntoView({ block: "start", behavior: riduciMovimento() ? "auto" : "smooth" });
    if (dati.puo_caricare) {
      const valida = dati.regole.categorie.some((c) => c.codice === richiesta);
      setCarica({ categoria: valida ? richiesta : null });
    }
    const resto = new URLSearchParams(params);
    resto.delete("documenti");
    setParams(resto, { replace: true });
  }, [richiesta, dati, params, setParams]);

  const tutti = dati ? [...dati.documenti, ...dati.tolti] : [];
  const aperto = tutti.find((d) => d.id === apertoId) || null;
  const gruppi = dati
    ? dati.regole.categorie
        .map((c) => ({ ...c, documenti: dati.documenti.filter((d) => d.categoria === c.codice) }))
        .filter((g) => g.documenti.length)
    : [];
  const quantiTolti = dati ? dati.tolti.length + dati.eliminati.length : 0;

  const caricato = (riga) => {
    const eraVersione = Boolean(carica?.documento);
    setCarica(null);
    toast.success(eraVersione ? `Versione ${riga.versione} caricata` : "Documento caricato");
    leggi();
    // Dopo una versione nuova si torna sul documento: si vede subito la v2 attuale e la v1 superata.
    if (eraVersione) setApertoId(riga.documento_id);
  };

  let corpo;
  if (errore && !dati) {
    corpo = (
      <div className="grid gap-inside">
        <p className="text-sm text-muted-foreground">L'archivio non si è caricato: {errore}</p>
        <Button type="button" variant="outline" size="sm" className="h-11 w-fit sm:h-8" onClick={leggi}>
          Riprova
        </Button>
      </div>
    );
  } else if (!dati) {
    corpo = (
      <div className="grid gap-group" aria-busy="true" aria-label="Caricamento documenti">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else {
    corpo = (
      <>
        {gruppi.length ? (
          gruppi.map((g) => (
            <section key={g.codice} className="grid grid-cols-1 gap-micro" aria-labelledby={`documenti-gruppo-${g.codice}`} data-testid={`documenti-gruppo-${g.codice}`}>
              <h3 id={`documenti-gruppo-${g.codice}`} className="text-xs font-medium text-muted-foreground">
                {g.etichetta} <span className="tabular-nums">· {g.documenti.length}</span>
              </h3>
              <ul className="-mx-inside grid grid-cols-1">
                {g.documenti.map((d) => (
                  <RigaDocumento key={d.id} doc={d} onApri={setApertoId} />
                ))}
              </ul>
            </section>
          ))
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="documenti-vuoto">
            {dati.puo_caricare
              ? "Nessun documento. Rilievi, disegni, il contratto firmato e le foto del cantiere si caricano qui e restano sulla scheda."
              : "Nessun documento caricato."}
          </p>
        )}

        <DallaPiattaforma generati={generati} onCambiato={onCambiato} chi={chi} />

        {quantiTolti ? (
          <section className="grid grid-cols-1 gap-micro border-t border-border pt-group">
            <button
              type="button"
              onClick={() => setMostraTolti((v) => !v)}
              aria-expanded={mostraTolti}
              aria-controls="documenti-tolti"
              className="-mx-inside flex min-h-11 items-center gap-inside rounded-md px-inside text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              data-testid="documenti-tolti-apri"
            >
              <ChevronDown className={cn("h-4 w-4 transition-transform", mostraTolti ? "" : "-rotate-90")} aria-hidden />
              Tolti dall'archivio <span className="tabular-nums">· {quantiTolti}</span>
            </button>
            {mostraTolti ? (
              <ul id="documenti-tolti" className="-mx-inside grid grid-cols-1" data-testid="documenti-tolti">
                {dati.tolti.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => setApertoId(d.id)}
                      data-testid={`documento-${d.id}`}
                      className="flex min-h-11 w-full items-center gap-group rounded-md px-inside py-inside text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground">{d.nome}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          tolto da {d.tolto.da_nome || "—"} · {giorno(d.tolto.at)}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    </button>
                  </li>
                ))}
                {dati.eliminati.map((e) => (
                  <li key={e.id} className="flex min-h-11 items-center gap-group px-inside py-inside">
                    <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground">{e.nome_file || "Documento"}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {fraseEvento(e)} · {giorno(e.created_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </>
    );
  }

  return (
    <Card ref={card} className="min-w-0 scroll-mt-card p-card" data-testid="cliente-documenti">
      <div className="flex items-center justify-between gap-group">
        <h2 className="text-base font-semibold text-foreground">Documenti</h2>
        {dati?.puo_caricare ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 sm:h-8"
            onClick={() => setCarica({ categoria: null })}
            data-testid="documenti-carica"
          >
            <Upload />
            Carica
          </Button>
        ) : null}
      </div>
      {/* grid-cols-1 = minmax(0,1fr): un nome lungo si tronca invece di allargare la colonna (a 390 px sbordava di 15). */}
      <div className="mt-group grid grid-cols-1 gap-card">{corpo}</div>

      <CaricaDocumento
        richiesta={carica}
        regole={dati?.regole}
        clienteId={clienteId}
        onChiudi={() => setCarica(null)}
        onCaricato={caricato}
      />
      <DettaglioDocumento
        documento={aperto}
        puoCaricare={Boolean(dati?.puo_caricare)}
        puoRimettere={Boolean(dati?.puo_rimettere)}
        onChiudi={() => setApertoId(null)}
        onVersioneNuova={(d) => {
          setApertoId(null);
          setCarica({ documento: d });
        }}
        onTogli={(d) => {
          setApertoId(null);
          setDaTogliere(d);
        }}
        onCambiato={leggi}
      />
      <TogliDialogo
        documento={daTogliere}
        onChiudi={() => setDaTogliere(null)}
        onTolto={() => {
          setDaTogliere(null);
          toast.success("Tolto dall'archivio: resta fra i tolti");
          leggi();
        }}
      />
    </Card>
  );
}
